import { assign, cloneDeep, concat, find, findIndex, fromPairs, isEmpty, keys, map, pick, pickBy } from 'lodash'

import type { IUser } from '~/types/users'
import type { IVendorSettings, IVendorSettingsNeedsToBeUpdatedCustomField } from '~/types/vendors'
import { formValidators, getValidator } from '~/utils'
import constraints from '~/utils/constraints'
import { transformBirthdate } from '~/utils/validators/birthdate'

enum AccountFormFields {
  FIRST_NAME = 'firstName',
  LAST_NAME = 'lastName',
  EMAIL = 'email',
  PHONE = 'phone',
  TELEPHONE = 'telephone',
  BIRTHDATE = 'birthdate',
  NEW_PASSWORD = 'newPassword',
  CONFIRM_PASSWORD = 'confirmPassword',
  ACCEPTED_TERMS_AND_CONDITIONS = 'acceptedTermsAndConditions',
  ALLOW_ADVERTISING = 'allowAdvertising',
  ACCEPTED_PRIVACY_POLICY = 'acceptedPrivacyPolicy',
}

const checkboxFields = [
  AccountFormFields.ACCEPTED_TERMS_AND_CONDITIONS,
  AccountFormFields.ALLOW_ADVERTISING,
  AccountFormFields.ACCEPTED_PRIVACY_POLICY,
]

interface FormFieldConfig {
  name?: AccountFormFields
  type?: 'password' | 'tel' | 'text' | 'email'
  label?: string
  placeholder?: string
  formatterName?: string
  rulesConstraints?: {
    default?: Record<string, any>
    required?: Record<string, any>
  }
}

const formFieldConfigs = {
  [AccountFormFields.FIRST_NAME]: {
    type: 'text',
    label: 'Nombres',
    placeholder: 'Nombres',
    formatterName: 'wordOnly',
    rulesConstraints: {
      required: pick(constraints.firstName, 'presence'),
    },
  },
  [AccountFormFields.LAST_NAME]: {
    type: 'text',
    label: 'Apellidos',
    placeholder: 'Apellidos',
    formatterName: 'wordOnly',
    rulesConstraints: {
      required: pick(constraints.lastName, 'presence'),
    },
  },
  [AccountFormFields.EMAIL]: {
    type: 'email',
    label: 'Correo',
    placeholder: 'Correo',
    rulesConstraints: {
      default: pick(constraints.email, 'email'),
      required: pick(constraints.email, 'presence'),
    },
  },
  [AccountFormFields.PHONE]: {
    type: 'tel',
    label: 'Celular',
    placeholder: 'Número de celular',
    formatterName: 'phone',
    rulesConstraints: {
      required: pick(constraints.phone, 'presence'),
    },
  },
  [AccountFormFields.TELEPHONE]: {
    type: 'tel',
    label: 'Teléfono',
    placeholder: 'Número de teléfono',
    formatterName: 'phone',
    rulesConstraints: {
      required: {
        presence: {
          message: 'Ingresa tu teléfono',
          allowEmpty: false,
        },
      },
    },
  },
  [AccountFormFields.NEW_PASSWORD]: {
    type: 'password',
    label: 'Nueva contraseña',
    placeholder: 'Nueva contraseña',
    rulesConstraints: {
      default: pick(constraints.newPassword, 'length'),
      required: pick(constraints.newPassword, 'presence'),
    },
  },
  [AccountFormFields.CONFIRM_PASSWORD]: {
    type: 'password',
    label: 'Confirmar contraseña',
    placeholder: 'Confirmar contraseña',
    rulesConstraints: {
      default: pick(constraints.confirmPassword, ['equality', 'length']),
      required: pick(constraints.confirmPassword, 'presence'),
    },
  },
}

const defaultAccountFormFields: IVendorSettingsNeedsToBeUpdatedCustomField[] = [
  {
    name: AccountFormFields.FIRST_NAME,
    rules: { required: true },
  },
  {
    name: AccountFormFields.LAST_NAME,
    rules: { required: true },
  },
  {
    name: AccountFormFields.EMAIL,
    rules: { required: true },
  },
  {
    name: AccountFormFields.PHONE,
    rules: { required: true },
  },
  {
    name: AccountFormFields.TELEPHONE,
    rules: { required: false },
  },
]

function getFormFieldConfig(customField: IVendorSettingsNeedsToBeUpdatedCustomField): FormFieldConfig {
  if (!Object.values<string>(AccountFormFields).includes(customField.name)) {
    throw new Error(`Field ${customField.name} is not supported`)
  }

  const customFieldName = customField.name as AccountFormFields

  const fieldConfig = formFieldConfigs[customFieldName]

  return {
    name: customFieldName,
    ...fieldConfig,
  }
}

function getSupportedCustomFields(vendorSettings: IVendorSettings) {
  if (isEmpty(vendorSettings?.needsToBeUpdated?.customFields)) {
    const hasBirthdate = vendorSettings?.signup?.birthdate

    const fields = cloneDeep(defaultAccountFormFields)

    if (hasBirthdate) {
      fields.push({
        name: AccountFormFields.BIRTHDATE,
        rules: {
          required: true,
        },
      })
    }

    return fields
  }

  const fields = cloneDeep(vendorSettings.needsToBeUpdated.customFields)

  /**
   * If the newPassword field exists, the confirmPassword
   * field must be automatically added, since they are
   * mutually dependent.
   */
  const newPasswordFieldIndex = findIndex(fields, { name: AccountFormFields.NEW_PASSWORD })

  const newPasswordField = fields[newPasswordFieldIndex]

  if (newPasswordField) {
    fields.splice(newPasswordFieldIndex + 1, 0, {
      name: AccountFormFields.CONFIRM_PASSWORD,
      rules: {
        required: newPasswordField.rules?.required,
      },
    })
  }

  return fields
}

export function getSupportedCustomFieldConfigs(vendorSettings: IVendorSettings) {
  return getSupportedCustomFields(vendorSettings).map(getFormFieldConfig)
}

export function getFormInitialValues(user: IUser, vendorSettings: IVendorSettings) {
  const fields = getSupportedCustomFields(vendorSettings)

  return pick(user, concat(map(fields, 'name'), checkboxFields))
}

export function getSubmitPayload(formValues: Record<string, any>, vendorSettings: IVendorSettings) {
  const fields = getSupportedCustomFields(vendorSettings)

  const fieldNames = concat(map(fields, 'name'), checkboxFields)

  const payload = pick(formValues, fieldNames)

  if (payload.birthdate) {
    payload.birthdate = transformBirthdate(payload.birthdate)
  }

  return payload
}

export function onValidateFormValues(formValues: Record<string, unknown>, vendorSettings: IVendorSettings) {
  const fields = getSupportedCustomFields(vendorSettings)

  const formConstraints = fromPairs(
    fields
      .map<any>(field => {
        const fieldConfig = formFieldConfigs[field.name as AccountFormFields]

        const fieldRules = fieldConfig?.rulesConstraints

        if (!fieldRules) {
          return false
        }

        const fieldRuleKeys = concat(keys(pickBy(field?.rules, Boolean)), 'default')

        const fieldConstraints = fieldRuleKeys.reduce((acc, value) => {
          return assign(acc, fieldRules[value])
        }, {})

        if (isEmpty(fieldConstraints)) {
          return false
        }

        return [field.name, fieldConstraints]
      })
      .filter(Boolean),
  )

  const errors = getValidator(formConstraints)(formValues)

  const birthDateConfig = find(fields, { name: AccountFormFields.BIRTHDATE })

  const { birthdate } = formValidators.birthdateField({
    birthdate: formValues.birthdate,
    settings: vendorSettings?.signup?.birthdate && !!birthDateConfig,
    isRequired: birthDateConfig?.rules?.required,
  })

  return {
    ...errors,
    birthdate,
  }
}
